/**
 * GrowthSalesBreakdown — Daily revenue and orders chart (premium design)
 */
import React, { useState, useEffect } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, BarChart3, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 shadow-xl text-white">
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-semibold" style={{ color: p.color }}>
          {p.name}: {p.value.toFixed(2)}
        </p>
      ))}
    </div>
  );
};

export default function GrowthSalesBreakdown({ authAxios, days }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState("area");

  useEffect(() => {
    const fetchSales = async () => {
      try {
        setLoading(true);
        const res = await authAxios.get(`/growth/sales?days=${days}`);
        setData(res.data);
      } catch (err) {
        console.error("Error fetching sales:", err);
        toast.error("Erro ao carregar vendas");
      } finally {
        setLoading(false);
      }
    };

    fetchSales();
  }, [days, authAxios]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid md:grid-cols-3 gap-4">
          {Array(3).fill(0).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-96 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Sem dados de vendas</p>
        </CardContent>
      </Card>
    );
  }

  const { daily, total_revenue, total_orders, avg_daily_revenue } = data;
  const peakRevenue = Math.max(...daily.map(d => d.revenue));
  const peakDay = daily.find(d => d.revenue === peakRevenue)?.date || "-";

  return (
    <div className="space-y-6">
      {/* Summary Cards with Icons */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-2">
                  Receita Total
                </p>
                <p className="text-2xl font-bold text-slate-900">€{total_revenue.toFixed(2)}</p>
                <p className="text-xs text-slate-500 mt-2">Período selecionado</p>
              </div>
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-2">
                  Total de Pedidos
                </p>
                <p className="text-2xl font-bold text-slate-900">{total_orders}</p>
                <p className="text-xs text-slate-500 mt-2">Período selecionado</p>
              </div>
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-2">
                  Receita Média/Dia
                </p>
                <p className="text-2xl font-bold text-slate-900">€{avg_daily_revenue.toFixed(2)}</p>
                <p className="text-xs text-slate-500 mt-2">Pico: {peakDay}</p>
              </div>
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart Card */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-200 pb-4">
          <div>
            <CardTitle className="text-lg font-bold">Receita Diária</CardTitle>
            <p className="text-xs text-slate-600 mt-1">Visualize tendências de receita e pedidos</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setChartType("area")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                chartType === "area"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Área
            </button>
            <button
              onClick={() => setChartType("bar")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                chartType === "bar"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Barras
            </button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <ResponsiveContainer width="100%" height={400}>
            {chartType === "area" ? (
              <AreaChart data={daily} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" fontSize={12} stroke="#64748b" />
                <YAxis fontSize={12} stroke="#64748b" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="url(#colorRevenue)" name="Receita" />
              </AreaChart>
            ) : (
              <BarChart data={daily} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" fontSize={12} stroke="#64748b" />
                <YAxis fontSize={12} stroke="#64748b" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="revenue" fill="#3b82f6" name="Receita" radius={[8, 8, 0, 0]} />
                <Bar dataKey="orders" fill="#10b981" name="Pedidos" radius={[8, 8, 0, 0]} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
